import { forwardRef, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';

import { trackEvent } from 'lib/tracking';

/**
 * A component that allows to declaratively trigger an analytic event.
 * It is used in conjunction with Segment. specify the event's name and options
 * to include when the event is triggered.
 *
 * Using `React.forwardRef` to support rendering inside other components that
 * provide a ref prop (e.g. `NextLink`).
 *
 * @param {object} props
 */
const EventTrack = forwardRef(function EventTrack(
  { children, shouldTrack = true, track = {}, ...restProps },
  ref
) {
  function handleClick() {
    const { event, ...properties } = track;
    trackEvent(event, {
      ...properties,
    });
  }

  const child = Children.only(children);

  return cloneElement(child, {
    ref,
    ...restProps,
    ...(shouldTrack && {
      onClick: (...args) => {
        // In case we receive an `onClick` prop, either explicitly or from a
        // parent of `EventTrack` (e.g. `NextLink` adds such a prop), call it.
        restProps.onClick?.(...args);
        // If the child has an `onClick` as well, call it.
        child.props.onClick?.(...args);
        handleClick();
      },
    }),
  });
});

EventTrack.propTypes = {
  children: PropTypes.node,
  /** Trigger tracking on or off */
  shouldTrack: PropTypes.bool,
  /** Track object for analytics purposes */
  track: PropTypes.shape({
    event: PropTypes.string,
    category: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default EventTrack;
